import { useEffect, useRef, useState } from "react";
import Lottie from "lottie-react";
import TrackChangesIcon from "@mui/icons-material/TrackChanges";
import { useScriptMode } from "../../../contexts/ScriptModeContext";
import { meterComplete, meterTotal, pencilPosition } from "../../../lib/meter";
import walkingPencil from "../../../assets/lottie/walking_pencil.json";
import completedStar from "../../../assets/lottie/completed_animation.json";
import { HatimIcon, MuzakereIcon } from "../../../components/ui/mdiIcons";

const ICONS = { goals: TrackChangesIcon, hatim: HatimIcon, muzakere: MuzakereIcon };
/** Dilimler arası boşluk, şerit genişliğine oranla (yaklaşık 6px). */
const GAP_PX = 6;
const PENCIL = 64;
/** Kalem animasyonunda zemin çizgisinin yüksekliği (938 / 1080). */
const FEET = 0.87;

/**
 * Günün metresi (mobil TerakkimMeter'in web karşılığı): hedefler, hatim ve
 * müzakere dilimleri; en çok ilerleyen başta. Bitmemiş ilk dilimin dolduğu
 * noktada yürüyen kalem durur, dilim bitince sonrakine kayar, artışta kısa
 * bir an büyür. Hepsi bitince çubuk yeşile döner, kalem söner ve sağ uçta
 * tamamlandı yıldızı döner; bitmediyse sağ uçta toplam yüzde yazar.
 */
export default function DayMeter({ steps, loading }) {
  const { t } = useScriptMode();
  const trackRef = useRef(null);
  const [width, setWidth] = useState(0);
  const [bump, setBump] = useState(false);
  const lastPos = useRef(null);

  useEffect(() => {
    const el = trackRef.current;
    if (!el) return undefined;
    const update = () => setWidth(el.getBoundingClientRect().width);
    update();
    if (typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [loading]);

  const complete = !loading && meterComplete(steps);
  const total = meterTotal(steps);
  const gapRatio = width > 0 ? GAP_PX / width : 0;
  const pos = loading ? null : pencilPosition(steps, gapRatio);

  // Artışta kalem kısa bir an büyür.
  useEffect(() => {
    if (pos === null) {
      lastPos.current = null;
      return undefined;
    }
    const grew = lastPos.current !== null && pos > lastPos.current + 0.0001;
    lastPos.current = pos;
    if (!grew) return undefined;
    setBump(true);
    const timer = setTimeout(() => setBump(false), 450);
    return () => clearTimeout(timer);
  }, [pos]);

  if (!loading && (!steps || steps.length === 0)) return null;

  const fill = complete
    ? "bg-[#2E9E6A] dark:bg-[#34B37A]"
    : "bg-terakkiPrimary dark:bg-white";

  return (
    <div
      data-tour="meter"
      className="shrink-0 motion-safe:animate-rise-in [animation-delay:.3s] flex items-center gap-4 px-5 py-3 rounded-2xl bg-terakkiSurface border border-terakkiBorder dark:bg-white/5 dark:border-white/15"
    >
      <div ref={trackRef} className="relative flex-1 flex gap-[6px] pt-1">
        {loading
          ? [0, 1, 2].map((i) => (
              <div key={i} className="flex-1 flex flex-col gap-1.5">
                <div className="h-1.5 rounded-full bg-terakkiBrandMist dark:bg-white/10 animate-pulse" />
                <div className="h-3 w-1/2 rounded bg-terakkiBrandMist dark:bg-white/10 animate-pulse" />
              </div>
            ))
          : steps.map((step) => {
              const Icon = ICONS[step.key];
              const pct = Math.round(step.progress * 100);
              const done = pct >= 100;
              return (
                <div key={step.key} className="flex-1 min-w-0 flex flex-col gap-1.5">
                  <div className="h-1.5 rounded-full bg-terakkiBrandMist dark:bg-white/10 overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-[width] duration-500 ${fill}`}
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                  <span
                    className={`flex items-center gap-1 text-sm truncate ${
                      done
                        ? "text-[#2E9E6A] dark:text-[#7BE3A8] font-SemiBold"
                        : "text-terakkiNeutral dark:text-white/70"
                    }`}
                  >
                    {Icon ? <Icon sx={{ fontSize: 13 }} aria-hidden /> : null}
                    {t(step.label)}
                  </span>
                </div>
              );
            })}

        {/* Çizginin üstünde yürüyen kalem; metrenin boyunu değiştirmez */}
        {!loading && width > 0 ? (
          <div
            aria-hidden
            className="pointer-events-none absolute transition-[left,opacity] duration-700 ease-in-out"
            style={{
              width: PENCIL,
              height: PENCIL,
              top: 4 - PENCIL * FEET,
              left: (pos ?? 1) * width - PENCIL / 2,
              opacity: pos === null ? 0 : 1,
            }}
          >
            <div
              className="w-full h-full transition-transform duration-200"
              style={{
                transformOrigin: `50% ${FEET * 100}%`,
                transform: bump ? "scale(1.3)" : "scale(1)",
              }}
            >
              <Lottie animationData={walkingPencil} loop autoplay style={{ width: PENCIL, height: PENCIL }} />
            </div>
          </div>
        ) : null}
      </div>

      <div className="relative w-12 h-11 shrink-0 flex items-center justify-center">
        {complete ? (
          <div className="pointer-events-none absolute" style={{ width: 96, height: 96 }} aria-hidden>
            <Lottie animationData={completedStar} loop autoplay style={{ width: 96, height: 96 }} />
          </div>
        ) : loading ? null : (
          <span className="text-base font-SemiBold text-terakkiPrimaryDark dark:text-white">
            %{Math.round(total * 100)}
          </span>
        )}
      </div>
    </div>
  );
}
